import { useEffect, useState } from "react";
import { anuncioDoDia, proporcaoDe, type PosicaoId } from "@nhomi/core";
import { registrarClique, registrarImpressao, watchAnuncios } from "@/data/anuncios";
import { dayKey, photoURL } from "@/data/plates";
import { useSession } from "@/lib/auth";
import { usePlano } from "@/lib/plano";
import type { Anuncio as AnuncioT } from "@nhomi/core";

/* ============================================================
   O espaço de anúncio

   Uma peça só para as três posições. Quem decide QUAL anúncio aparece é
   o core — a mesma função que a prévia do painel chama, senão o que se
   vê ao configurar não é o que vai ao ar.

   A PEÇA É A IMAGEM, e nada além dela. Título e texto existem no
   cadastro só pra achar o anúncio na lista do painel — eles não chegam
   aqui. O motivo é o de sempre em anúncio: o anunciante já produz a
   arte com a tipografia, a cor e o logo dele, e o que a gente escrevia
   por cima nunca combinava com nada. Um espaço só, do tamanho
   declarado, resolve os dois lados.

   Quatro regras que o componente garante sozinho:

   - assinante não vê nada, e o espaço nem existe (não é `display: none`,
     é não desenhar);
   - sem anúncio para a posição, nada é desenhado. Caixa vazia com
     "publicidade" escrito é pior que anúncio nenhum;
   - SEM IMAGEM também não desenha. Desde que o texto saiu, um anúncio
     sem arte é um retângulo com o selo "Patrocinado" e mais nada —
     ocupa espaço do feed e não anuncia coisa alguma;
   - o rótulo é obrigatório e sai daqui, não do cadastro. Depender de o
     anunciante escrever "patrocinado" é depender de quem tem motivo pra
     não escrever — e as duas lojas cobram o rótulo.
   ============================================================ */

/** Compartilhado entre as posições: os anúncios são poucos e mudam pouco. */
let cache: AnuncioT[] | null = null;

export function Anuncio({ posicao }: { posicao: PosicaoId }) {
  const { assinante } = usePlano();
  const { user } = useSession();
  const [lista, setLista] = useState<AnuncioT[]>(cache ?? []);
  const [imagem, setImagem] = useState<string | null>(null);

  useEffect(() => {
    if (assinante) return;
    return watchAnuncios((l) => { cache = l; setLista(l); });
  }, [assinante]);

  const escolhido = assinante ? null : anuncioDoDia(lista, posicao, dayKey());
  const caminho = escolhido?.imagemPath ?? null;

  useEffect(() => { void photoURL(caminho).then(setImagem); }, [caminho]);

  /* A impressão é contada quando o anúncio existe na tela, não quando o
     componente monta: sem anúncio escolhido não há o que contar, e o
     assinante nem chega aqui. */
  const idEscolhido = escolhido?.id;
  useEffect(() => {
    if (!idEscolhido || !user) return;
    void registrarImpressao(user.uid, idEscolhido);
  }, [idEscolhido, user]);

  if (!escolhido) return null;
  // A imagem é a peça: sem ela não há anúncio, só um buraco no feed.
  if (!escolhido.imagemPath) return null;

  /* Enquanto a URL do Storage não chega, o espaço fica reservado com a
     altura certa. Sem isso o feed inteiro pula pra baixo quando a
     imagem carrega — e é o pulo que faz alguém tocar no que não queria. */
  const botao = posicao === "prato" && escolhido.cta && escolhido.url;

  /* A caixa é PROPORÇÃO, não altura fixa, e vem do core — a mesma
     função que a prévia do painel usa. Com altura fixa a caixa muda de
     proporção junto com a largura da tela, e a mesma arte é cortada de
     um jeito no celular largo e de outro no estreito. Aí a medida que o
     painel promete ao anunciante não vale em aparelho nenhum. */
  const caixa = { aspectRatio: proporcaoDe(posicao) ?? undefined };

  const conteudo = (
    <>
      <span className="anuncio-selo">Patrocinado</span>
      {imagem
        ? <img className="anuncio-imagem" style={caixa} src={imagem}
            alt={escolhido.alt || "Anúncio"} />
        : <div className="anuncio-vazio" style={caixa} aria-hidden="true" />}
      {botao && (
        <div className="anuncio-corpo">
          <span className="btn btn-primary anuncio-botao">{escolhido.cta}</span>
        </div>
      )}
    </>
  );

  const classe = `anuncio anuncio-${posicao}`;

  /* Sem endereço, não é link: âncora sem destino engana o dedo e some do
     leitor de tela. E `noreferrer` porque o destino é de terceiro. */
  if (!escolhido.url) return <div className={classe}>{conteudo}</div>;

  return (
    <a className={classe} href={escolhido.url} target="_blank" rel="noopener noreferrer"
      onClick={() => { if (user) void registrarClique(user.uid, escolhido.id); }}>
      {conteudo}
    </a>
  );
}
